import { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { Globe, Check } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function LocaleSwitcher() {
    const { locale, available, t } = useT();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (! wrapperRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    if (! available || available.length < 2) return null;

    const active = available.find((l) => l.code === locale) ?? available[0];

    const pick = (code) => {
        setOpen(false);
        if (code === locale) return;
        // Optimistic <html dir> + lang flip; Inertia reload confirms.
        const next = available.find((l) => l.code === code);
        if (next && typeof document !== 'undefined') {
            document.documentElement.dir = next.rtl ? 'rtl' : 'ltr';
            document.documentElement.lang = code;
        }
        router.post(`/_locale/${code}`, {}, { preserveScroll: true });
    };

    return (
        <div ref={wrapperRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => ! v)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium hover:bg-secondary/40"
                title={t('common.label.change_language', 'Change language')}
            >
                <Globe className="size-3.5" />
                {active?.code?.toUpperCase()}
            </button>
            {open && (
                <div className="absolute right-0 z-[60] mt-1 w-52 max-h-80 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
                    <p className="border-b border-border px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('common.label.language', 'Language')}
                    </p>
                    {available.map((l) => (
                        <button
                            key={l.code}
                            type="button"
                            onClick={() => pick(l.code)}
                            className={cn(
                                'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/40',
                                l.code === locale && 'bg-accent/5',
                            )}
                        >
                            <span>
                                <span className="font-medium">{l.native_name || l.name}</span>
                                <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">{l.code}</span>
                            </span>
                            {l.code === locale && <Check className="size-3.5 text-accent" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
